import { useEffect, useState } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { useTranslation } from "next-i18next";
import ms from "ms";
import {
  ActivityHeadline,
  ActivityWrapper,
} from "@components/shared/mobile/activityMobile";
import {
  MdOutlineChat,
  MdOutlineClose,
  MdOutlinePerson,
  MdOutlineTimer,
} from "react-icons/md";
import Image from "next/image";
import { HiOutlineUser } from "react-icons/hi";
import { BsCalendarCheck, BsFillLightningFill } from "react-icons/bs";
import { IoBookOutline } from "react-icons/io5";
import { BiMapPin } from "react-icons/bi";
import Cookies from "js-cookie";
import { events, track } from "@shared/tracking";
import { useRouter } from "next/router";
import { en, es, fr, isoDuration } from "@musement/iso-duration";
import { cn } from "@lib/utils";
import { useCurrencyRate } from "@hooks/useCurrencyRate";

export type Activity = {
  id: number;
  url: string;
  name: string;
  description: string;
  address: string;
  providerImgUrl: string;
  score: number;
  lang: string;
  duration: string;
  reviews: number;
  freeCancelation: boolean;
  originalPrice: number;
  minimumPrice: number;
  currency: string;
};

type SharedEventsPayload = {
  city_id: string;

  section: string;
} & (
  | {
      experience_id: string;
    }
  | {
      place_to_visit_id: string;
    }
);

type ActivitiesCardProps = {
  activities: Activity[];
  onExpand: () => void;
  onCollapse: () => void;
  spotTopExpId: number;
  eventsPayload: SharedEventsPayload;
};

export const ActivitiesCard = ({
  activities,
  onExpand,
  onCollapse,
  spotTopExpId,
  eventsPayload,
}: ActivitiesCardProps) => {
  const { t } = useTranslation();
  const router = useRouter();

  const [isExpanded, setIsExpanded] = useState(
    router.query.expand_activities === "true"
  );

  const [expandedActivity, setExpandedActivity] =
    useState<`${number}-${number}`>();

  isoDuration.setLocales(
    {
      en,
      es,
      fr,
    },
    {
      fallbackLocale: "en",
    }
  );

  const expand = () => {
    setIsExpanded(true);
    onExpand();
  };

  const collapse = () => {
    setIsExpanded(false);
    onCollapse();
  };

  const currencyRate = useCurrencyRate();

  const price = activities.reduce(
    (price, curr) => {
      const activityPrice = Number(curr.minimumPrice) || 0;
      const minPrice =
        activityPrice > price.value ? price.value : activityPrice;

      const currency = currencyRate.symbol;
      return {
        value: Math.round(currencyRate.rate * minPrice),
        currency,
      };
    },
    {
      value: Math.round(currencyRate.rate * Number(activities?.[0]?.minimumPrice) || 0),
      currency: currencyRate.symbol,
    }
  );


 
  const score = activities.reduce<number>(
    (score, curr) =>
      Number(curr?.score < score ? score?.toFixed(1) : curr.score?.toFixed(1)),
    Number(activities?.[0]?.score?.toFixed(1).replace(/\.0+$/, ""))
  );

  const onExpandActivity = (activityId: number, activityIndex: number) => {
    // setExpandedActivity(`${activityId}-${activityIndex}`);
    // get url params

    router.push({
      pathname: "/spotprofile/[id]/activity/[activityId]",
      query: {
        ...router.query,
        activityId,
        experience_id: spotTopExpId.toString(),
        expand_activities: activities.length > 1 ? "true" : "false",
        section: eventsPayload.section,
      },
    });

    track(events.CHECK_ACTIVITY_DETAILS, {
      ...eventsPayload,
      activity_id: activityId.toString(),
    });
  };

  const onBookActivity = (activityId: number) => {
    track(events.BOOK_ACTIVITY, {
      ...eventsPayload,
      activity_id: activityId.toString(),
    });
  };

  if (activities.length === 1) {
    const [activity] = activities;
    const isExpanded = `${activity.id}-0` === expandedActivity;
    return (
      <div
        className={`absolute z-50 bottom-0 w-full max-w-lg ${
          isExpanded ? "h-full" : ""
        }`}
      >
        {isExpanded && (
          <div
            className="w-full h-full cursor-pointer"
            onClick={() => setExpandedActivity(undefined)}
          />
        )}
        <ActivityWrapper className="!w-full pb-1">
          <ActivityHeadline
            activitiesNumber={activities.length}
            isFullScreenVideo
            isActivityExpanded
            type={"activity"}
          />
          <div className="flex flex-col w-full max-h-[85dvh]">
            <Activity
              activity={activity}
              isExpanded={isExpanded}
              onExpand={() => onExpandActivity(activity.id, 0)}
              onCollapse={() => setExpandedActivity(undefined)}
              onBook={() => onBookActivity(activity.id)}
              canAddExtraMargin={!!expandedActivity}
              section={eventsPayload.section}
              isSingleActivity
            />
          </div>
        </ActivityWrapper>
        <div className="flex w-full">
          <ActivityBookingInfo
            activityId={activities[0].id}
            className="md:rounded-b-xl"
          />
        </div>
      </div>
    );
  }

  return isExpanded ? (
    <div className="absolute z-50 w-full h-[100dvh] md:h-[95dvh] md:rounded-2xl bg-secondary-color/50 top-0">
      <ActivityWrapper className="absolute z-50 w-full max-w-lg bottom-0 md:rounded-2xl">
        {!expandedActivity && (
          <ActivityHeadline
            activitiesNumber={activities.length}
            isFullScreenVideo
            isActivityExpanded={isExpanded}
            setIsActivityExpanded={collapse}
            type={t("TITLE_ACTIVITIES")}
            trackingId={"shrinkActivitiesBtn_" + spotTopExpId}
          />
        )}
        <div className="flex flex-col w-full overflow-y-auto max-h-[90dvh] pt-2">
          {activities.map((activity, idx) => {
            return (
              <Activity
                key={`${activity.id}-${idx}`}
                activity={activity}
                isExpanded={`${activity.id}-${idx}` === expandedActivity}
                onExpand={() => onExpandActivity(activity.id, idx)}
                onCollapse={() => setExpandedActivity(undefined)}
                onBook={() => onBookActivity(activity.id)}
                canAddExtraMargin={
                  !!expandedActivity && idx === activities.length - 1
                }
                section={eventsPayload.section}
                isSomeActivityExpanded={!!expandedActivity}
              />
            );
          })}
        </div>
      </ActivityWrapper>
    </div>
  ) : (
    <div
      className="absolute z-50 w-full max-w-lg bottom-0 rounded-2xl h-[120px]"
      onClick={expand}
    >
      <div
        className="p-1 mx-2 mb-1 bg-secondary-color/90 rounded-xl"
        id={"expandActivitiesBtn_" + spotTopExpId}
      >
        <div className="flex items-center justify-between p-2 border gap-x-1 border-primary-color rounded-xl">
          <div className="text-white">
            <p className="text-sm font-medium text-white capitalize">
              <span className="text-lg text-primary-color">
                {activities.length}
              </span>{" "}
              {t(
                activities.length === 1 ? "TITLE_ACTIVITY" : "TITLE_ACTIVITIES"
              )}
            </p>
            <p className="text-sm font-medium text-white uppercase">
              {t("FROM")}{" "}
              <span className="text-lg text-primary-color">
                {price.value}
                {price.currency}
              </span>
            </p>
          </div>
          <div className="flex items-center justify-center w-14">
            <CircularProgressbar
              styles={buildStyles({
                pathColor: `#00F3CD`,
                trailColor: "#2a423e",
                textColor: "#00F3CD",
              })}
              maxValue={5}
              strokeWidth={6}
              value={score}
              text={`${Number(score.toFixed(1))}/5`}
            />
          </div>
          <button className="bg-primary-color px-3 py-4 uppercase rounded-xl font-medium border-2 border-[#34B09D]">
            {t("SEE_ACTIVITIES")}
          </button>
        </div>
      </div>

      <div className="flex w-full md:rounded-2xl z-[999]">
        <ActivityBookingInfo
          activityId={activities[0].id}
          className="md:rounded-b-xl"
        />
      </div>
    </div>
  );
};

export const ActivityContainer = ({
  children,
  onClick,
  usePrimaryColorBorder = false,
  isSingleActivity = false,
  additionalClasses = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  usePrimaryColorBorder?: boolean;
  isSingleActivity?: boolean;
  additionalClasses?: string;
}) => {
  return (
    <div
      className={`w-full border ${additionalClasses} ${
        usePrimaryColorBorder ? "border-primary-color" : "border-[#4B4B4B]"
      } outline-none rounded-xl bg-[#121212] flex flex-col px-4 ${
        isSingleActivity ? "pb-1 mb-1" : "pb-4 mb-2"
      }`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const Activity = ({
  activity,
  isExpanded,
  onExpand,
  onCollapse,
  onBook,
  canAddExtraMargin,
  section,
  isSomeActivityExpanded = false,
  isSingleActivity = false,
}: {
  activity: Activity;
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onBook: () => void;
  canAddExtraMargin: boolean;
  section: string;
  isSomeActivityExpanded?: boolean;
  isSingleActivity?: boolean;
}) => {
  return (
    <div className={canAddExtraMargin ? "mb-28" : undefined}>
      {isExpanded ? (
        <ActivityDetails
          activity={activity}
          onClick={onCollapse}
          onBookActivity={onBook}
          trackingId={`${
            section === "experiences"
              ? "btnBookActivityExperience"
              : "btnBookActivityMustSee"
          }_${activity.id}`}
        />
      ) : (
        !isSomeActivityExpanded && (
          <ActivityCard
            trackingId={`${
              section === "experiences"
                ? "btnSpotTopExperiencesMobile"
                : "btnSpotTopMustSeeMobile"
            }_${activity.id}`}
            activity={activity}
            onClick={onExpand}
            isSingleActivity={isSingleActivity}
          />
        )
      )}
    </div>
  );
};

const ActivityCard = ({
  trackingId,
  activity,
  onClick,
  isSingleActivity = false,
}: {
  trackingId: string;
  activity: Activity;
  onClick: () => void;
  isSingleActivity?: boolean;
}) => {
  const { t: translate } = useTranslation();

  isoDuration.setLocales(
    {
      en,
      es,
      fr,
    },
    {
      fallbackLocale: "en",
    }
  );

  const currencyRate = useCurrencyRate();

  return (
    <ActivityContainer onClick={onClick} isSingleActivity={isSingleActivity}>
      <h4
        className={`w-full pt-1 font-bold text-white ${
          isSingleActivity ? "text-sm" : ""
        }`}
      >
        {activity.name}
      </h4>
      <div className="flex items-center justify-between w-full">
        {/* MIDDLE PART */}
        <div className="flex flex-col w-full items-center justify-between p-px">
          <div className="flex items-center justify-between w-full gap-x-2">
            <p className="text-xl font-bold text-primary-color">
              {Number(activity.score.toFixed(1))}/5
            </p>
            {/* 2ND MED-RIGHT-ITEM */}
            <div
              className={`grid gap-y-0.5 w-full pl-3 ${
                isSingleActivity ? "grid-cols-2" : ""
              }`}
            >
              {!!activity?.reviews && (
                <div
                  className={`flex items-center text-white ${
                    isSingleActivity ? "col-span-2" : ""
                  }`}
                >
                  <MdOutlinePerson
                    color="white"
                    size={isSingleActivity ? 12 : 14}
                    className="mr-0.5"
                  />
                  <p
                    className={`${
                      isSingleActivity ? "text-[10px]" : "text-xs"
                    } whitespace-nowrap`}
                  >
                    {activity.reviews +
                      " " +
                      translate("ACTIVITY_CARD_OPINIONS")}
                  </p>
                </div>
              )}
              {!!activity.duration &&
                activity.duration !== "0" &&
                activity?.duration !== "P0D" && (
                  <div className={"flex items-center text-white w-[210%] "}>
                    <MdOutlineTimer
                      color="white"
                      size={isSingleActivity ? 12 : 14}
                      className="mr-0.5"
                    />
                    <p
                      className={`${
                        isSingleActivity ? "text-[10px]" : "text-xs "
                      }`}
                    >
                      {isoDuration(
                        isNaN(+activity.duration)
                          ? activity.duration
                          : ms(+activity.duration * 60 * 1000, {
                              long: true,
                            })
                      ).humanize("en")}
                    </p>
                  </div>
                )}
              {!!activity?.lang && (
                <div className={"flex items-center text-white"}>
                  <MdOutlineChat
                    color="white"
                    size={isSingleActivity ? 12 : 14}
                    className="mr-0.5"
                  />
                  <p
                    className={`${
                      isSingleActivity ? "text-[10px]" : "text-xs"
                    }`}
                  >
                    {activity?.lang}
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col items-center justify-center">
              <h5
                className={`font-bold text-center capitalize text-primary-color whitespace-nowrap ${
                  isSingleActivity ? "text-2xl" : "text-3xl"
                }`}
              >
                {+activity.originalPrice > 0
                  ? 
                  (
                  activity.id % 2 == 0 ?
                  <div>
                    <div className="-mt-1.5">{Math.round(currencyRate.rate * activity.originalPrice)}{currencyRate.symbol}</div>
                    <div className="text-slate-400 line-through text-xl -mt-1.5">‎{ Math.round(currencyRate.rate * Math.round(activity.originalPrice *1.12)) + currencyRate.symbol+'‎' }‎ </div>
                  </div>
                  : <div>{Math.round(currencyRate.rate * activity.originalPrice)}{currencyRate.symbol}</div>
                  )
                  : translate("FREE")}
              </h5>
              {activity.freeCancelation && (
                <h6 className="text-white text-xs whitespace-nowrap">
                  {translate("FREE_CANCELLATION")}
                </h6>
              )}
            </div>
          </div>
        </div>
        {/* RIGHT PART */}
      </div>
      <div className="flex flex-col items-center justify-center pt-2 pb-1">
        <button
          id={trackingId}
          className={`w-full px-1 font-bold rounded-md bg-primary-color ${
            isSingleActivity ? "py-1 text-sm" : "py-2"
          }`}
        >
          {translate("SPOT_PROFILE_CHECK_ACTIVITY")}
        </button>
      </div>
    </ActivityContainer>
  );
};

const ActivityDetails = ({
  activity,
  onClick,
  onBookActivity,
  trackingId,
}: {
  activity: Activity;
  onClick: () => void;
  onBookActivity: () => void;
  trackingId: string;
}) => {
  const { t: translate } = useTranslation();

  return (
    <ActivityContainer usePrimaryColorBorder>
      <div className="flex items-center justify-between py-2 mb-1 border-b-4 border-primary-color">
        <h4 className="w-full py-1 font-bold text-white">{activity.name}</h4>
        <div
          className="grid pl-2 text-white place-items-center"
          role="button"
          onClick={onClick}
        >
          {/* close expanded activity */}
          <MdOutlineClose size={32} />
        </div>
      </div>

      <div className="flex items-center justify-between py-1">
        <h6 className="w-full text-sm font-semibold text-white">
          {translate("ABOUT_THIS_EXPERIENCE")}
        </h6>
        <div className="w-20">
          <Image
            alt="provider"
            src={activity.providerImgUrl}
            width={100}
            height={100}
            className="w-full h-full aspect-auto"
          />
        </div>
      </div>
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center justify-between gap-x-2">
          <div className="flex items-center justify-center w-10">
            <CircularProgressbar
              styles={buildStyles({
                pathColor: `#00F3CD`,
                trailColor: "#2a423e",
                textColor: "#00F3CD",
              })}
              maxValue={5}
              strokeWidth={6}
              value={activity.score}
              text={`${Number(activity.score.toFixed(1))}/5`}
            />
          </div>
          {!!activity?.reviews && (
            <div className="flex items-center justify-center text-gray-100">
              <HiOutlineUser className="text-lg" />
              <p className="text-xs text-center whitespace-nowrap">
                {activity.reviews} {translate("OPINIONS")}
              </p>
            </div>
          )}
        </div>
        {activity.freeCancelation && (
          <div className="flex items-center justify-center gap-x-2">
            <BsCalendarCheck className="text-sm text-gray-100 " />
            <p className="text-xs text-center whitespace-nowrap text-primary-color">
              {translate("FREE_CANCELLATION")}
            </p>
          </div>
        )}
      </div>
      <div className="w-full pb-2 text-sm">
        <p className="text-white">{activity.description}</p>
        {/* <button className="flex items-center justify-center py-2 text-primary-color">
          <span className="border-b border-primary-color">
            {translate("MORE_DETAILS")}
          </span>
          <MdChevronRight className="text-xl" />
        </button> */}
      </div>
      {!!activity.duration && activity.duration != "0" && (
        <div className="flex items-center py-2 gap-x-1">
          <MdOutlineTimer className="text-xl text-white" />
          <p className="text-sm text-primary-color">
            {translate("DURATION")}{" "}
            {isoDuration(activity.duration).humanize("en")}
          </p>
        </div>
      )}
      <div className="flex items-center py-2 gap-x-1">
        <MdOutlineChat className="text-xl text-white" />
        <p className="text-sm text-primary-color">{activity.lang}</p>
      </div>
      {!!activity.address && activity.address !== "undefined" && (
        <>
          <div className="flex items-center py-2 gap-x-1">
            <BiMapPin className="text-xl text-white" />
            <p className="text-sm text-primary-color">
              {translate("MEETING_POINT")}
            </p>
          </div>
          <div className="text-white">
            <p className="text-sm">{activity.address}</p>
          </div>
        </>
      )}

      <div className="flex flex-col">
        <div className="flex items-center py-2 gap-x-1">
          <IoBookOutline className="text-xl text-white" />
          <p className="text-sm text-primary-color">
            {translate("BOOK_THE_ACTIVITY")}
          </p>
        </div>
        <div className="flex flex-col w-full p-2 rounded-md h-36 bg-slate-200">
          <h4 className="text-sm font-semibold ">
            {translate("AVAILABILITY_AND_PRICES")}
          </h4>
          <p className="text-sm font-light">
            {translate("DATES_SELECT_LABEL")}
          </p>
          {/* generate a select with 3 options of randon dates */}
          <select className="w-full h-10" name="" id="">
            <option value="">1</option>
            <option value="">2</option>
            <option value="">3</option>
          </select>

          <button className="w-full h-10 text-sm font-semibold bg-primary-color ">
            {translate("CHECK_AVAILABILITY")}
          </button>

          {/* <select name="" id="">
            <option value="">2</option>
          </select> */}
        </div>
      </div>

      <ActivityFooter
        activityId={activity.id}
        activityPrice={
          activity.minimumPrice && activity.minimumPrice != 0
            ? `${activity.minimumPrice}€`
            : translate("FREE")
        }
        onBookClick={onBookActivity}
      />
    </ActivityContainer>
  );
};

export const ActivityFooter = ({
  activityId,
  activityPrice,
  onBookClick,
}: {
  activityId: number;
  activityPrice: string;
  onBookClick: () => void;
}) => {
  const { t } = useTranslation();

  const currencyRate = useCurrencyRate();

  return (
    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-lg bg-white text-secondary-color z-50 md:rounded-b-xl">
      <ActivityBookingInfo activityId={activityId} />
      <div className="flex items-center justify-between px-3 py-1">
        <div className="flex justify-center items-center w-1/2">
          <p className="font-semibold px-2 text-4xl uppercase text-[#49D3BE]">
            {activityPrice.indexOf('€') > -1 ? Math.round(currencyRate.rate * (Number(activityPrice.replace('€','')))) + currencyRate.symbol : activityPrice}
          </p>
        </div>
        <button
          className="bg-primary-color text-center w-1/2 py-2 rounded-lg border-2 border-[#34B09D] shadow text-xl font-semibold uppercase"
          onClick={(e) => {
            e.stopPropagation();
            onBookClick();
          }}
        >
          {t("BOOK")}
        </button>
      </div>
    </div>
  );
};

export const ActivityBookingInfo = ({
  activityId,
  className,
}: {
  activityId: number;
  className?: string;
}) => {
  const { t } = useTranslation();

  const [bookingsCount, setBookingsCount] = useState<string>();

  useEffect(() => {
    let activityBookingsCount = Cookies.get(
      `activity_${activityId}_bookings_count`
    );

    if (!activityBookingsCount) {
      activityBookingsCount = (Math.random() * 60).toFixed(0);
      Cookies.set(
        `activity_${activityId}_bookings_count`,
        activityBookingsCount,
        {
          expires: 1, // 1 day
          path: "/",
          sameSite: "strict",
        }
      );
    }

    setBookingsCount(activityBookingsCount);
  }, []);

  const icons = Array.from({
    length: 3,
  }).map((_, i) => <BsFillLightningFill key={i} className="text-xs" />);

  return (
    <div
      className={cn(
        "flex justify-center items-center bg-[#FFCE40]/90 p-2 w-full h-[30px]",
        className
      )}
    >
      {icons}
      <p className="text-xs font-semibold px-0.5">
        {bookingsCount} {t("BOOKING_IN_THE_LAST_24_HOURS")}
      </p>
      {icons}
    </div>
  );
};
